<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2022-2024, Vector Software BV                             **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Vector Software BV. Unauthorized copying or disclosure of this file,    **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
*****************************************************************************
-->

<template>
    <div
        id="controlPanel"
        v-if="customerId"
        :class="controlPanelStyle"
    >
        <v-expand-transition v-if="$auth.subscription == 'VCT1'">
            <v-card max-height="90vh">
                <v-progress-linear
                    :active="statusLoading"
                    indeterminate
                    absolute
                    top
                    height="8"
                    color="orange"
                >
                </v-progress-linear>
                <v-container fluid>
                    <v-row no-gutters>
                        <v-col cols="4">
                            <v-icon :color="batteryPercentage > 20 ? 'rgba(0, 0, 0, 0.54)' : 'red'">{{
                                batteryIcons[batteryIndex] }}</v-icon>
                            {{ batteryPercentage }}%
                            <v-icon :color="online ? 'rgba(0, 0, 0, 0.54)' : 'red'">{{ online ? 'mdi-network-strength-4' :
                                'mdi-network-strength-off'
                            }}</v-icon>
                        </v-col>
                        <v-col>
                            <v-chip :class="status">{{ language.t("Vector.Statuses." + status) }}</v-chip>
                        </v-col>
                        <v-col
                            cols="1"
                            class="d-flex justify-end"
                        >
                            <v-btn
                                fab
                                x-small
                                color="success"
                                :disabled="account == 0"
                                @click="notificationCenter()"
                            >
                                <v-icon>
                                    mdi-bell
                                </v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
                <v-card-actions>
                </v-card-actions>
                <v-card-actions
                    v-if="taskSelected && (status == 'IDLE' || status == 'READY') && lineSelected && selectedTaskId == lineTask"
                >
                    <v-btn
                        block
                        @click="setRouteOnVector()"
                    > Laad route</v-btn>
                </v-card-actions>
                <v-card-actions class="card-actions">
                    <v-btn
                        block
                        v-if="!working && status != 'ERROR' && account != 0"
                        :disabled="!ready"
                        color="green"
                        v-on:click="startVectorDialog = true"
                    >
                        Start
                    </v-btn>
                    <v-btn
                        block
                        v-if="working && status != 'ERROR'"
                        color="error"
                        v-on:click="stopVector()"
                    >
                        Stop
                    </v-btn>
                    <v-btn
                        block
                        v-if="status == 'ERROR' && error != 'OBSTACLE'"
                        v-on:click="releaseVector()"
                    >
                        {{ language.t("Vector.Buttons.release") }}
                    </v-btn>
                    <v-btn
                        disabled
                        block
                        v-if="account == 0"
                    > {{ language.t("Vector.Buttons.select") }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-expand-transition>
        <OpenTaskNotification
            :taskId="openTaskId"
            :openTaskDialog="openTaskDialog"
            v-on:close="dismissOpenTaskNotif"
            v-on:open="openTaskFromVector"
        />
        <StartVectorNotification
            :startVectorDialog="startVectorDialog"
            v-on:close="dismissStartVectorNotif"
            v-on:startVector="startVector"
        />
    </div>
</template>

<script>
import OpenTaskNotification from "./OpenTaskNotification"
import StartVectorNotification from "./StartVectorNotification.vue"

export default {
    components: {
        OpenTaskNotification,
        StartVectorNotification,
    },
    data() {
        return {
            websocket: null,
            statusLoading: false,

            status: 'NOTSELECTED',
            last_status: '',
            websocketStatus: null,
            error: '',
            warning: '',

            working: false,
            ready: false,
            online: false,

            taskSelected: false,
            selectedTaskId: null,
            lineData: null,
            lineTask: null,
            lineSelected: false,

            batteryPercentage: 0,
            batteryIcons: ["mdi-battery-10", "mdi-battery-40", "mdi-battery-60", "mdi-battery-80", "mdi-battery"],
            batteryIndex: 0,
            openTaskDialog: false,
            openTaskId: 0,

            startVectorDialog: false,
        };
    },
    props: {
        /* Map.vue reference */
        map: {
            required: true,
        },
    },
    mounted: function () {
        setTimeout(this.setDeviceStatusTimer, 2000);
    },
    computed: {
        customerId() {
            return this.$auth.customerId;
        },
        controlPanelStyle: function () {
            return {
                floating: !this.$vuetify.breakpoint.xsOnly,
                inline: this.$vuetify.breakpoint.xsOnly,
            };
        },
        account() {
            return this.$selectedVector.accountId;
        }
    },
    watch: {
        account(val, oldVal) {
            this.getDeviceStatus();
            this.subscribeToChannel(val, oldVal)
        }
    },
    methods: {
        connectToWebsocket() {
            this.$axios.get(route('api.auth.getwsvariables')).then(res => {
                this.websocket = new WebSocket(`${res.data.websocketHost}/app/Tyker?accountId=${res.data.accountId}&randomKey=${res.data.randomKey}`);

                this.websocket.addEventListener("message", (event) => {
                    console.log("Message from server ", event.data);
                    this.parseWebsocketMsg(event.data);
                });

                this.websocket.onclose = (event) => {
                    console.log("WebSocket closed: ", event);
                    this.connectToWebsocket();
                };

                this.websocket.onerror = (event) => {
                    console.log("WebSocket error: ", event);
                    this.connectToWebsocket();
                };
            });
        },
        subscribeToChannel(id, oldId) {
            if (!this.websocket) return;
            if (id !== 0) {
                var subscribe = {
                    event: "pusher:subscribe",
                    data: {
                        auth: "",
                        channel: `vectorStatusEvent.${id}`
                    }
                }
                this.websocket.send(JSON.stringify(subscribe));
            }
            if (oldId !== 0) {
                var unsubscribe = {
                    event: "pusher:unsubscribe",
                    data: {
                        auth: "",
                        channel: `vectorStatusEvent.${oldId}`
                    }
                }

                this.websocket.send(JSON.stringify(unsubscribe));
            }
        },
        startVector() {
            this.$axios.post(route('api.vector.start', this.account)).then(() => {
                this.getDeviceStatus();
            });
        },
        stopVector() {
            this.$axios.post(route('api.vector.stop', this.account)).then(() => {
                this.getDeviceStatus();
            });
        },
        releaseVector() {
            this.$axios.post(route('api.vector.release', this.account)).then(() => {
                this.getDeviceStatus();
            });
        },
        setRouteOnVector() {
            this.$axios.post(route('api.vector.selectLine', [this.account, this.selectedTaskId]), this.lineData).then(() => {
                this.getDeviceStatus();
            });
        },
        parseWebsocketMsg(message) {
            var msg = JSON.parse(message);
            if (msg.event == "pusher:connection_established") {
                if (this.account) {
                    this.subscribeToChannel(this.account, 0)
                }
                return;
            }
            if (msg.data) {
                var data = JSON.parse(msg.data);
                this.websocketStatus = data.status;
                this.parseActivityStatus(this.websocketStatus);
            }
        },
        parseActivityStatus(statusUpdate) {
            this.$emit("onRemoveAlert", "vector");

            this.status = statusUpdate.split(",")[0];
            if (this.status == "ERROR") {
                this.openTaskNotification(Number(statusUpdate.split(",")[1]));

                var errors = statusUpdate.split(",");
                errors.shift(); //Remove ERROR
                errors.shift(); //remove taskId
                errors.forEach(error => {
                    this.parseError(error);
                });
            } else if (this.status == "WARNING") {
                this.openTaskNotification(Number(statusUpdate.split(",")[1]));
                this.working = true;

                var warnings = statusUpdate.split(",");
                warnings.shift(); //Remove WARNING
                warnings.shift(); //remove taskId
                warnings.forEach(warning => {
                    this.parseWarning(warning);
                });
            } else if (this.status == "WORKING") {
                this.working = true;
                this.openTaskNotification(Number(statusUpdate.split(",")[1]));
            } else if (this.status == "IDLE") {
                this.working = false;
                this.ready = false;
                this.openTaskNotification(Number(statusUpdate.split(",")[1]));
            } else if (this.status == "READY") {
                this.ready = true;
                this.working = false;
                this.openTaskNotification(Number(statusUpdate.split(",")[1]));
            } else if (this.status == "RESTART") {
                this.working = false;
                this.ready = false;
            } else if (this.status == "SHUTDOWN") {
                this.working = false;
                this.ready = false;
            }
            this.last_status = this.status;
        },
        parseError(error) {
            switch (error) {
                case "OBSTACLE":
                    this.$emit("onAddAlert", "vector", this.language.t("Vector.Errors.obstacle"), "error");
                    break;
                case "OBSTACLESTOPPED":
                    this.$emit("onAddAlert", "vector", this.language.t("Vector.Errors.obstacleStopped"), "error");
                    break;
                case "BUMPER":
                    this.$emit("onAddAlert", "vector", this.language.t("Vector.Errors.safetyBumper"), "error");
                    break;
                case "RC-ESWITCH":
                    this.$emit("onAddAlert", "vector", this.language.t("Vector.Errors.rcESwitch"), "error");
                    break;
                case "ESWITCH-RIGHT":
                    this.$emit("onAddAlert", "vector", this.language.t("Vector.Errors.eSwitchRight"), "error");
                    break;
                case "ESWITCH-LEFT":
                    this.$emit("onAddAlert", "vector", this.language.t("Vector.Errors.eSwitchLeft"), "error");
                    break;
                case "LIVEEDGE":
                    this.$emit("onAddAlert", "vector", this.language.t("Vector.Errors.liveEdge"), "error");
                    break;
                case "RC-OUT_OF_RANGE":
                    this.$emit("onAddAlert", "vector", this.language.t("Vector.Errors.rcOutOfRange"), "error");
                    break;
                case "RC-OFF":
                    this.$emit("onAddAlert", "vector", this.language.t("Vector.Errors.rcOff"), "error");
                    break;
                default:
                    console.log('unknown error: ' + error);
                    break;
            }
            this.error = error;
        },
        parseWarning(warning) {
            switch (warning) {
                case "OBSTACLE":
                    this.$emit("onAddAlert", "vector", this.language.t("Vector.Warnings.obstacle"), "error");
                    break;
                default:
                    console.log('unknown warning: ' + warning);
                    break;
            }
            this.warning = warning;
        },
        parseHeartbeat(heartbeat) {
            if (heartbeat.online) { //one minute
                this.online = true;
                this.status == "IDLE"
            } else {
                this.online = false;
                this.status = 'NOTCONNECTED';
                this.last_status = this.status;
            }

            var parsedString = heartbeat.description.toUpperCase().split("BATT:")[1];
            this.batteryPercentage = (parsedString * 100).toFixed(0);
            this.changeBatteryIcon();
        },
        getDeviceStatus() {
            if (this.$auth.subscription == 'VCT1') {
                if (!this.websocket || this.websocket.readyState != WebSocket.OPEN) {
                    this.connectToWebsocket();
                }

                this.statusLoading = true;
                this.$axios.get(route("api.devices.heartbeat")).then((res) => {
                    if (res.data === 'No devices') { this.statusLoading = false; return; }
                    res.data.forEach((device) => {
                        if (device.accountId == this.account) {
                            this.parseHeartbeat(device.latest_activity);
                        }
                    });
                    if (this.status != 'NOTCONNECTED') {
                        this.$axios.get(route("api.devices.status.last")).then((res) => {
                            if (res.data === 'No devices') { this.statusLoading = false; return; }
                            res.data.forEach((device) => {
                                if (device.accountId == this.account) {
                                    this.parseActivityStatus(device.activity[0].description);
                                }
                            });

                        });
                    }
                    this.statusLoading = false;
                });
            }
        },
        setDeviceStatusTimer() {
            this.getDeviceStatus();
            setTimeout(
                function () {
                    this.setDeviceStatusTimer();
                }.bind(this),
                30 * 1000
            );
        },
        notificationCenter() {
            this.$emit("menuSelected", "notificationCenter");
        },
        setSelectedTask(isSelected, id) {
            this.taskSelected = isSelected;
            this.selectedTaskId = id;
        },
        setLineData(lineData, taskId) {
            lineData ? this.lineSelected = true : this.lineSelected = false;
            this.lineData = lineData;
            this.lineTask = taskId;
        },
        changeBatteryIcon() {
            if (this.batteryPercentage >= 0 && this.batteryPercentage <= 20) {
                this.batteryIndex = 0;
            } else if (this.batteryPercentage >= 21 && this.batteryPercentage <= 40) {
                this.batteryIndex = 1;
            } else if (this.batteryPercentage >= 41 && this.batteryPercentage <= 60) {
                this.batteryIndex = 2;
            } else if (this.batteryPercentage >= 61 && this.batteryPercentage <= 80) {
                this.batteryIndex = 3;
            } else if (this.batteryPercentage >= 81 && this.batteryPercentage <= 100) {
                this.batteryIndex = 4;
            }
        },
        openTaskNotification(taskId) {
            if (taskId == 0) return;
            this.$selectedTask.taskId = taskId;
            if ((!this.map.task || this.map.task.taskId != taskId) && this.openTaskId != taskId) {
                this.openTaskId = taskId;
                this.openTaskDialog = true;
            }
        },
        openTaskFromVector(task) {
            this.$emit("closeTask");
            this.$emit("openTask", task);
            this.openTaskDialog = false;

        },
        dismissOpenTaskNotif() {
            this.openTaskDialog = false;
        },
        dismissStartVectorNotif() {
            this.startVectorDialog = false;
        }
    }
};
</script>
  
<style scoped>
#controlPanel {
    z-index: 75;
    /* margin-left: 5px;*/
}

#controlPanel form {
    display: flex;
    flex: 1 1 auto;
    margin: -12px;
}

.floating {
    position: absolute;
    bottom: 70px;
    left: 23px;
    width: 400px;
}

.inline {
    position: absolute;
    width: calc(100vw - 18px - 18px);
    bottom: 70px;
    left: 18px;
}
</style>
  
<style>
/* Manual fix for this bug: https://github.com/vuetifyjs/vuetify/issues/13133 */
/* v-autocomplete using item slot with v-list-item-title and v-list-item-subtitle is rendered too wide */
/* .v-autocomplete__content,
.v-list-item-bugfix {
    width: 400px !important;
    max-height: 80% !important; /* this is just to increase the height 
} */

#tasklist .v-input__slot {
    margin-bottom: 0px;
}

#tasklist .v-text-field__details {
    min-height: unset;
    height: 0px;
}

.v-text-field.v-text-field--enclosed .v-text-field__details {
    margin-bottom: 0px;
}

.v-chip.v-chip--no-color.theme--light.v-size--default.ERROR {
    background: red;
}

.v-chip.v-chip--no-color.theme--light.v-size--default.IDLE .v-chip.v-chip--no-color.theme--light.v-size--default.INIT .v-chip.v-chip--no-color.theme--light.v-size--default.NOTCONNECTED {
    background: lightgray;
}

.v-chip.v-chip--no-color.theme--light.v-size--default.WORKING {
    background: green;
}

.v-chip.v-chip--no-color.theme--light.v-size--default.CHARGING {
    background: lightskyblue;
}
</style>